import RenderEmpty from '../ItemDataCategoryTable/RenderEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ArticleAssetRow from './partials/ArticleAssetRow';
import {
    extractUniqueAssetClassesByDescription,
    extractUniqueAssetDescriptionType
} from '../../../helpers';
import { createPaginationMeta } from '../../../helpers/paginate-helpers';
import ArticleAssetPreviewCard from './partials/ArticleAssetPreviewCard';
import Modal from '../../utils/Modal';
import ArticleDigitalAssetModalGallery from './partials/ArticleDigitalAssetModalGallery';
import { selectors } from '../../../reducers/appReducer';
import { getLocaleFromLanguageCode } from '../../../intl-helpers';

/**
 * Le props assets e assetsMeta arrivano vari parent component sopra, da ItemDataCategory
 * @param assets
 * @param assetsMeta
 * @param category
 * @returns {JSX.Element}
 * @constructor
 */

/**
 * @param assets
 * @param assetsMeta
 * @param category
 * @param showActiveFilter
 */
const ArticleDigitalAssetContainer = ({ assets, assetsMeta, category, showActiveFilter = false, articleDetails = null }) => {
    const language = useSelector(state => selectors.getLanguage(state))
    const locale = getLocaleFromLanguageCode(language)

    // State locale per la current page
    const [currentPage, setCurrentPage] = useState(0)

    // In selected filter una empty string serve per indicare tutti gli elementi -> '' = tutti
    const [selectedFilter, setSelectedFilter] = useState('')
    const [selectedDescription, setSelectedDescription] = useState('')
    const [filteredMeta, setFilteredMeta] = useState(assetsMeta)
    const [showPreview, setShowPreview] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [startingIndex, setStartingIndex] = useState(0)

    // Estraggo tutte le asset_classes uniche, filtrate per la description selezionata
    const uniqueAssetClasses = useMemo(() => {
        return extractUniqueAssetClassesByDescription(assets, selectedDescription, locale)
    }, [assets, selectedDescription, locale])

    // Helper per ottenere la label del filtro attivo da mostrare
    const getSelectedFilterLabel = () => {
        if (!selectedFilter) return '';
        const found = uniqueAssetClasses.find(opt => opt.code === selectedFilter);
        return found ? found.label : selectedFilter;
    }
    // Estraggo tutte le description degli asset uniche
    const uniqueAssetDescription = useMemo(() => {
        return extractUniqueAssetDescriptionType(assets)
    }, [assets])

    // Determino se la category supporta il filtro per description (linea, serie, modello)
    const supportsDescriptionFilter = ['line_digital_asset', 'serie_digital_asset', 'model_digital_asset'].includes(category?.key)

    // Aggiungo uno stato per tenere traccia degli assets filtrati
    const [filteredAssets, setFilteredAssets] = useState(assets);

    // Seleziono di default la prima description se ce ne sono più di una
    useEffect(() => {
        if (supportsDescriptionFilter && uniqueAssetDescription.length > 1) {
            setSelectedDescription(uniqueAssetDescription[0])
        }
    }, [supportsDescriptionFilter, uniqueAssetDescription])

    // Resetto il filtro tipologia quando cambia la description (potrebbe non essere più disponibile)
    useEffect(() => {
        setSelectedFilter('')
    }, [selectedDescription])

    useEffect(() => {
        if (!selectedFilter && !selectedDescription) {
            // Se non c'è filtro, usa i meta originali e tutti gli assets
            setFilteredMeta(assetsMeta);
            setFilteredAssets(assets);
            setCurrentPage(0); // Reset alla prima pagina
        } else {
            const filtered = assets.filter(asset => {
                // Filtro per description
                const matchDescription = !selectedDescription || asset.description === selectedDescription;

                // Filtro per asset_classes
                let matchFilter = true;
                if (selectedFilter) {
                    matchFilter = asset.asset_classes?.some(cls => cls.code === selectedFilter);
                }

                return matchDescription && matchFilter;
            });

            // Ricalcola la paginazione con gli assets filtrati
            const newMeta = createPaginationMeta(filtered, 15); // stesso itemsPerPage
            setFilteredMeta(newMeta);
            setFilteredAssets(filtered);
            setCurrentPage(0); // Reset alla prima pagina quando cambia il filtro
        }
    }, [selectedFilter, selectedDescription, assets, assetsMeta])


    // State locale per la paginazione
    const currentChunk = filteredMeta?.chunks?.[currentPage];
    const displayItems = filteredMeta?.needsPagination && currentChunk
        ? currentChunk.items
        : (filteredMeta?.chunks?.[0]?.items || [])

    /// HANDLER
    // Handler per il cambio di filtro
    const handleFilterChange = (e) => {
        setSelectedFilter(e.target.value);
    }

    const handleDescriptionChange = (e) => {
        setSelectedDescription(e.target.value)
    }

    // Handler per lo switch showPreview
    const handleShowPreviewChange = () => {
        // setShowPreview(!showPreview)
        // prev ce lo fornisce direttamente React
        setShowPreview(prev => !prev)
    }

    // Handler per navigazione
    const goToNextPage = () => {
        if (currentPage < filteredMeta.totalPages - 1) {
            setCurrentPage(prev => prev + 1);
        }
    }

    const goToPrevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
    }

    // Handler per il toggle della modale
    const toggleModal = (idx) => {
        const nextIdx = currentPage > 0 ? idx + (currentPage * 15) : idx

        setStartingIndex(nextIdx)
        setShowModal(!showModal)
    }
    return assets.length > 0 ? (
        <div className="w-full article-assets-container">

            <Modal isOpen={showModal} modalRootClass="modal-ultrawide">
                <ArticleDigitalAssetModalGallery
                    assets={filteredAssets}
                    startingIndex={startingIndex}
                    onClose={toggleModal}
                    articleDetails={articleDetails}
                    locale={locale}
                    />
            </Modal>

            <div className="flex flex-row">
                <div className="basis-3/4">
                    <p className="mt-1 mb-1">
                        {/*<strong className="mr-2"> { category?.label ? category?.label : 'Assets' }</strong>*/}
                        {showActiveFilter && selectedFilter && (
                            <small className=" text-gray-600">
                                Filtro attivo: <strong>{getSelectedFilterLabel()}</strong>
                            </small>
                        )}

                        {showActiveFilter && selectedDescription && supportsDescriptionFilter && uniqueAssetDescription.length > 1 && (
                            <small className="ml-2 text-gray-600">
                                {category?.key === 'line_digital_asset' && 'Linea: '}
                                {category?.key === 'serie_digital_asset' && 'Serie: '}
                                {category?.key === 'model_digital_asset' && 'Modello: '}
                                <strong>{selectedDescription}</strong>
                            </small>
                        )}
                    </p>
                </div>
                <div className="ml-auto text-right">
                    <div className="form-group">
                        <label className="form-switch">
                            <input
                                type="checkbox"
                                onChange={handleShowPreviewChange}
                            />
                            <i className="form-icon" />
                            Anteprima
                        </label>
                    </div>

                </div>
            </div>

            <hr className="dp-hr-primary mb-3" />
            <div className="overflow-x-auto">
                {
                    showPreview && (
                        <div>
                            <div className="flex gap-2 pt-2">
                                <select
                                    value={selectedFilter}
                                    onChange={handleFilterChange}
                                    className="block px-2 py-1 text-xs font-medium text-gray-500 tracking-wider border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white">
                                    <option value="">Tipologia</option>
                                    {uniqueAssetClasses.map(option => (
                                        <option key={option.code} value={option.code}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {supportsDescriptionFilter && uniqueAssetDescription.length > 1 && (
                                    <select
                                        value={selectedDescription}
                                        onChange={handleDescriptionChange}
                                        className="block px-2 py-1 text-xs font-medium text-gray-500 tracking-wider border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        {uniqueAssetDescription.map(description => (
                                            <option key={description} value={description}>
                                                {description}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 pt-2">
                            {displayItems.map((item, idx) => {
                                return (
                                    <ArticleAssetPreviewCard
                                        item={item}
                                        locale={locale}
                                        clickCallback={() => {
                                                toggleModal(idx)
                                            }
                                        }
                                        articleDetails={articleDetails}
                                        key={item?.id ?? idx}
                                    />
                                )
                            })}
                        </div>
                        </div>

                    )
                }
                {
                    !showPreview && (
                        <table className={`min-w-full bg-white border border-gray-200 text-sm table-digital-asset ${supportsDescriptionFilter ? 'table-support-description-filter' : ''}`}>
                            <thead className="bg-primary">
                            <tr>
                                <th className="px-3 py-2 text-left border-b border-primary">
                                    <select
                                        value={selectedFilter}
                                        onChange={handleFilterChange}
                                        className="block w-full px-2 py-1 text-xs font-medium text-gray-500 tracking-wider border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Tipologia</option>
                                        {uniqueAssetClasses.map(option => (
                                            <option key={option.code} value={option.code}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                                {supportsDescriptionFilter && (
                                    <th className="px-3 py-2 text-left border-b border-primary">
                                        {uniqueAssetDescription.length > 1 ? (
                                            <select
                                                value={selectedDescription}
                                                onChange={handleDescriptionChange}
                                                className="block w-full px-2 py-1 text-xs font-medium text-gray-500 tracking-wider border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white">
                                                {uniqueAssetDescription.map(description => (
                                                    <option key={description} value={description}>
                                                        {description}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="text-xs font-medium text-white tracking-wider">
                                                {category?.key === 'line_digital_asset' && 'Linea'}
                                                {category?.key === 'serie_digital_asset' && 'Serie'}
                                                {category?.key === 'model_digital_asset' && 'Modello'}
                                            </span>
                                        )}
                                    </th>
                                )}
                                <th className="px-3 py-2 text-left text-xs font-medium text-white tracking-wider border-b border-primary">
                                    Nome File
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-white tracking-wider border-b border-primary">
                                    Peso
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-white tracking-wider border-b border-primary">
                                    Download
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {
                                displayItems.map((asset, idx) => (
                                    <ArticleAssetRow
                                        asset={asset}
                                        idx={idx}
                                        locale={locale}
                                        showDescription={supportsDescriptionFilter}
                                        articleDetails={articleDetails}
                                        key={asset?.id ?? idx}
                                    />
                                ))
                            }
                            </tbody>
                        </table>
                    )
                }

            </div>

            <div className="flex items-center justify-between mt-2 px-3">
                {
                    filteredMeta?.needsPagination && (
                        <div className="text-xs text-gray-700">
                            Pagina <span className="font-medium">{currentPage + 1}</span> di{' '}
                            <span className="font-medium">{filteredMeta.totalPages}</span>
                            {' '}({filteredMeta.totalItems} elementi{selectedFilter ? ' filtrati' : ''})
                        </div>
                    )
                }

                {
                    filteredMeta?.needsPagination && (
                        <div className="flex gap-1">
                            <button
                                onClick={goToPrevPage}
                                disabled={currentPage === 0}
                                className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                Precedente
                            </button>
                            <button
                                onClick={goToNextPage}
                                disabled={currentPage === filteredMeta.totalPages - 1}
                                className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                Successivo
                            </button>
                        </div>
                    )
                }
            </div>
        </div>
    ) : (
        <RenderEmpty />
    )
}

export default ArticleDigitalAssetContainer